import React, { useState, useEffect } from 'react';
import { X, Target, AlertCircle } from 'lucide-react';
import { Button, Input, SearchSelect, TextArea } from '../shared';
import { CategoryIconPicker, iconValue, parseIconId, suggestIconId, usableImageUrl } from '../shared/categoryIcons';

const CreateServiceCategoryModal = ({ 
 isOpen, 
 onClose, 
 onSave, 
 serviceTypes, 
 category = null, 
 isEdit = false,
 isLoading = false
}) => {
 const [formData, setFormData] = useState({
 category_name: '',
 emoji: '',
 category_description: '',
 service_type: '',
 icon_id: '',
 icon_url: '',
 is_active: true,
 is_featured: false,
 sort_order: 0
 });
 const [errors, setErrors] = useState({});

 useEffect(() => {
 if (isEdit && category) {
 setFormData({
 category_name: category.category_name || '',
 emoji: category.emoji || '',
 category_description: category.category_description || '',
 service_type: category.service_type || '',
 icon_id: parseIconId(category.icon_url) || (usableImageUrl(category.icon_url) ? '' : suggestIconId(category)),
 icon_url: usableImageUrl(category.icon_url) || '',
 is_active: category.is_active ?? true,
 is_featured: category.is_featured ?? false,
 sort_order: category.sort_order || 0
 });
 } else {
 setFormData({
 category_name: '',
 emoji: '',
 category_description: '',
 service_type: '',
 icon_id: '',
 icon_url: '',
 is_active: true,
 is_featured: false,
 sort_order: 0
 });
 }
 setErrors({});
 }, [isEdit, category, isOpen]);

 const validateForm = () => {
 const newErrors = {};

 if (!formData.category_name.trim()) {
 newErrors.category_name = 'Category name is required';
 }

 if (!formData.service_type) {
 newErrors.service_type = 'Service type is required';
 }

 if (formData.sort_order < 0) {
 newErrors.sort_order = 'Sort order must be a positive number';
 }

 setErrors(newErrors);
 return Object.keys(newErrors).length === 0;
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 
 if (!validateForm()) {
 return;
 }

 const customUrl = (formData.icon_url || '').trim();
 const iconId = formData.icon_id || suggestIconId(formData);
 const payload = {
 ...formData,
 icon_url: customUrl || iconValue(iconId) || null,
 };
 delete payload.icon_id;
 onSave(payload);
 };

 const handleInputChange = (field, value) => {
 setFormData(prev => ({
 ...prev,
 [field]: value
 }));
 
 // Clear error when user starts typing
 if (errors[field]) {
 setErrors(prev => ({
 ...prev,
 [field]: ''
 }));
 }
 };

 if (!isOpen) return null;

 return (
 <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
 <div className="relative top-4 sm:top-20 mx-auto p-4 sm:p-5 border w-full max-w-2xl shadow-sm rounded-md bg-surface m-4">
 {/* Header */}
 <div className="flex items-center justify-between mb-6">
 <div className="flex items-center">
 <div className="flex-shrink-0 h-8 w-8 bg-gradient-to-r from-ink to-neutral-900 rounded-lg flex items-center justify-center mr-3">
 <Target className="h-4 w-4 text-white" />
 </div>
 <h3 className="text-lg font-medium text-gray-900">
 {isEdit ? 'Edit Service Category' : 'Create Service Category'}
 </h3>
 </div>
 <button
 onClick={onClose}
 className="text-gray-400 hover:text-gray-600 transition-colors"
 >
 <X className="h-6 w-6" />
 </button>
 </div>

 {/* Form */}
 <form onSubmit={handleSubmit} className="space-y-6">
 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
 {/* Category Name */}
 <div className="md:col-span-2">
 <Input
 label="Category Name"
 type="text"
 value={formData.category_name}
 onChange={(e) => handleInputChange('category_name', e.target.value)}
 placeholder="Enter category name"
 error={errors.category_name}
 required
 />
 </div>

 {/* Emoji */}
 <div>
 <Input
 label="Emoji"
 type="text"
 value={formData.emoji}
 onChange={(e) => handleInputChange('emoji', e.target.value)}
 placeholder="e.g., 📸, 🎵, 🍽️"
 error={errors.emoji}
 maxLength="10"
 />
 <p className="mt-1 text-sm text-gray-500">
 Enter an emoji to represent this category
 </p>
 </div>

 {/* Service Type */}
 <div>
 <SearchSelect
 label="Service Type"
 value={formData.service_type}
 onChange={(value) => handleInputChange('service_type', value)}
 options={serviceTypes}
 placeholder="Select service type"
 error={errors.service_type}
 required
 />
 </div>

 {/* Sort Order */}
 <div>
 <Input
 label="Sort Order"
 type="number"
 value={formData.sort_order}
 onChange={(e) => handleInputChange('sort_order', parseInt(e.target.value) || 0)}
 placeholder="Enter sort order"
 error={errors.sort_order}
 min="0"
 />
 </div>

 <div className="md:col-span-2">
 <p className="mb-2 text-sm font-medium text-ink">Animated icon</p>
 <CategoryIconPicker
 value={formData.icon_url ? '' : formData.icon_id}
 onChange={(id) => setFormData((prev) => ({ ...prev, icon_id: id, icon_url: '' }))}
 />
 <p className="mt-2 text-sm text-gray-500">
 Shown on the homepage category tiles. The icon animates on its own.
 </p>
 <div className="mt-4">
 <Input
 label="Custom image URL"
 type="url"
 value={formData.icon_url}
 onChange={(e) => handleInputChange('icon_url', e.target.value)}
 placeholder="Optional. A photo URL replaces the animated icon."
 />
 </div>
 </div>

 {/* Description */}
 <div className="md:col-span-2">
 <TextArea
 label="Description"
 value={formData.category_description}
 onChange={(e) => handleInputChange('category_description', e.target.value)}
 placeholder="Enter category description"
 rows={3}
 />
 </div>

 {/* Status Checkboxes */}
 <div className="md:col-span-2">
 <div className="space-y-3">
 <div className="flex items-center">
 <input
 type="checkbox"
 id="is_active"
 checked={formData.is_active}
 onChange={(e) => handleInputChange('is_active', e.target.checked)}
 className="h-4 w-4 text-ink focus:ring-ink border-gray-300 rounded"
 />
 <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
 Active
 </label>
 </div>
 <p className="text-sm text-gray-500">
 Inactive categories will not be visible to users
 </p>
 
 <div className="flex items-center">
 <input
 type="checkbox"
 id="is_featured"
 checked={formData.is_featured}
 onChange={(e) => handleInputChange('is_featured', e.target.checked)}
 className="h-4 w-4 text-ink focus:ring-ink border-gray-300 rounded"
 />
 <label htmlFor="is_featured" className="ml-2 block text-sm text-gray-900">
 Featured
 </label>
 </div>
 <p className="text-sm text-gray-500">
 Featured categories will be highlighted on the homepage
 </p>
 </div>
 </div>
 </div>

 {/* Form Actions */}
 <div className="flex flex-col sm:flex-row justify-end gap-3 pt-6 border-t border-gray-200">
 <Button
 type="button"
 variant="secondary"
 onClick={onClose}
 disabled={isLoading}
 >
 Cancel
 </Button>
 <Button
 type="submit"
 disabled={isLoading}
 className="flex items-center"
 >
 {isLoading ? (
 <>
 <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
 {isEdit ? 'Updating...' : 'Creating...'}
 </>
 ) : (
 <>
 <Target className="w-4 h-4 mr-2" />
 {isEdit ? 'Update Category' : 'Create Category'}
 </>
 )}
 </Button>
 </div>
 </form>

 {/* Help Text */}
 <div className="mt-6 p-4 bg-surface-muted rounded-lg">
 <div className="flex">
 <AlertCircle className="h-5 w-5 text-ink-tertiary mt-0.5 mr-3 flex-shrink-0" />
 <div className="text-sm text-ink">
 <p className="font-medium mb-1">Tips for creating service categories:</p>
 <ul className="list-disc list-inside space-y-1 text-ink">
 <li>Use clear, descriptive names that users will understand</li>
 <li>Choose appropriate service types for better organization</li>
 <li>Set sort order to control display sequence (lower numbers appear first)</li>
 <li>Pick an animated icon. It shows on the homepage category row.</li>
 </ul>
 </div>
 </div>
 </div>
 </div>
 </div>
 );
};

export default CreateServiceCategoryModal;